import { useCallback, useState } from 'react';

import jstz from 'jstimezonedetect';
import moment from 'moment';

import { CustomControl, OldPasswordPolicy, PolicyData } from '../types/InsuranceReport';
import { HttpResponse } from '../../../../api/types/Http';
import { httpPost } from '../../../../components/utils';

interface ReportData {
    insuranceFileName: string | null;
    insuranceFileDate: string | null;
    insuranceGenerated?: boolean;
}

interface Params {
    startDate: moment.Moment;
    endDate: moment.Moment;
    emailPolicies: PolicyData;
    vpnPolicies: PolicyData;
    oldPassword: OldPasswordPolicy;
    useCustomerIcon: boolean;
    customControls: CustomControl[];
}

const useInsuranceReportGeneration = (onSuccess?: () => void) => {
    const [data, setData] = useState<ReportData>();
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [error, setError] = useState<string>();

    const triggerInsuranceReportGeneration = useCallback(async (insuranceSettings: Params) => {
        setIsLoading(true);
        setIsSuccessful(false);
        setError(undefined);

        try {
            const localTimeZone = encodeURIComponent(jstz.determine().name());

            const requestData = {
                localTimeZone: localTimeZone,
                insuranceSettings: insuranceSettings
            };

            const httpResponse = (await httpPost(
                `/insights/generateInsuranceData`,
                requestData
            )) as unknown as HttpResponse<ReportData>;
            const generatedData = httpResponse.data;

            setData({
                insuranceFileName: generatedData.insuranceFileName,
                insuranceFileDate: generatedData.insuranceFileDate
            });
            setIsSuccessful(true);
            onSuccess?.();
        } catch (err) {
            setError('Failed to generate insurance report');
        }

        setIsLoading(false);
    }, []);

    return { mutate: triggerInsuranceReportGeneration, data, isLoading, isSuccessful, error };
};

export default useInsuranceReportGeneration;
